import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  getObjectId,
  MeterFlowVolume,
  MeterFlowVolumeEntity,
  meterFlowVolumeSelector,
  Quantity,
  useGetMeterFlowVolumeQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  MeterFlowVolumeForm,
  MeterFlowVolumeFormValues
} from "../../components/form/MeterFlowVolumeForm/MeterFlowVolumeForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useMeterFlowVolumeMutation} from "../../hooks/useMeterFlowVolumeMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<MeterFlowVolumeFormValues>
}

export const MeterFlowVolumeFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <MeterFlowVolumeFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromMeterFlowVolume = (meterFlowVolume: MeterFlowVolumeEntity | undefined): Partial<MeterFlowVolumeFormValues> => {
  let quantity: Quantity | undefined = undefined;

  if (meterFlowVolume) {
    if (typeof meterFlowVolume.quantity !== 'undefined' && meterFlowVolume.unitOfMeasure) {
      quantity = {
        number: meterFlowVolume.quantity,
        units: meterFlowVolume.unitOfMeasure
      };
    }
  }

  return {
    timeGroup: meterFlowVolume?.timeGroup,
    date: adjustFromTimeZoneSafely(meterFlowVolume?.dttm),
    meter: meterFlowVolume?.meterId,
    quantity: quantity,
    quantityOverride: meterFlowVolume?.quantityOverride,
    quantityOverrideComment: meterFlowVolume?.quantityOverrideComment,
    freightType: meterFlowVolume?.freightTypeId,
  };
};

export const MeterFlowVolumeFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const selectMeterFlowVolume = useMemo(() => {
    return meterFlowVolumeSelector()
  }, [])

  const {currMeterFlowVolume, isLoading} = useGetMeterFlowVolumeQuery(id ?? skipToken, {
    selectFromResult: (result) => ({
      currMeterFlowVolume: selectMeterFlowVolume(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<MeterFlowVolumeFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromMeterFlowVolume(currMeterFlowVolume);
  }, [currMeterFlowVolume, formDefaults]);

  const title: string = useMemo(() => {
    if (currMeterFlowVolume && !copy) {
      return t("page.entity.meterflowvolume.form.title.edit", "Edit Meter Flow Volume", {ns: "common"});
    } else {
      return t("page.entity.meterflowvolume.form.title.new", "New Meter Flow Volume", {ns: "common"});
    }
  }, [currMeterFlowVolume, copy, t]);

  const {
    createMeterFlowVolume,
    updateMeterFlowVolume,
    isCreating,
    isUpdating,
  } = useMeterFlowVolumeMutation()

  const onMeterFlowVolumeSubmit = useCallback((formValues: MeterFlowVolumeFormValues) => {
    const patch: Partial<MeterFlowVolume> = {
      timeGroup: formValues.timeGroup.code,
      dttm: adjustToTimeZone(formValues.date).getTime(),
      meterId: getObjectId(formValues.meter),
      quantity: formValues.quantity.number,
      quantityOverride: formValues.quantityOverride,
      quantityOverrideComment: formValues.quantityOverrideComment,
      unitOfMeasure: formValues.quantity.units,
      freightTypeId: getObjectId(formValues.freightType),
      regionId: regionId,
    }

    if (currMeterFlowVolume && !copy) {
      const meterFlowVolume: Partial<MeterFlowVolume> = {
        ...currMeterFlowVolume.asTypeValue(),
        ...patch,
      };
      updateMeterFlowVolume(meterFlowVolume, () => navigate(-1))
    } else {
      const meterFlowVolume: Partial<MeterFlowVolume> = {
        ...patch,
      };
      createMeterFlowVolume(meterFlowVolume, () => navigate(-1))
    }
  }, [regionId, currMeterFlowVolume, copy, updateMeterFlowVolume, navigate, createMeterFlowVolume]);

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <MeterFlowVolumeForm
                  options={{
                    regionId: currMeterFlowVolume?.regionId ?? regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onMeterFlowVolumeSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};

